import React from "react";
import { useTable, usePagination, useSortBy, Column } from "react-table";
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Flex,
    IconButton,
    Text,
    Tooltip,
    Select,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Tfoot,
    chakra
} from "@chakra-ui/react";
import {
    ArrowRightIcon,
    ArrowLeftIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    TriangleDownIcon,
    TriangleUpIcon
} from "@chakra-ui/icons";



export type DataTableProps<Data extends object> = {
    data: Data[];
    columns: Column<Data>[];
    setPerPage: any,
    setPage: any,
    currentPage: number,
    perPage: number,
    totalPage: number,
};

export default function CustomTable({
    data,
    columns,
    setPerPage,
    setPage,
    currentPage,
    perPage,
    totalPage,
}: DataTableProps<any>) {
    // Use the state and functions returned from useTable to build your UI
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page, // Instead of using 'rows', we'll use page,
        // which has only the rows for the active page

        // The rest of these things are super handy, too ;)
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable(
        {
            columns,
            data,
            useControlledState: (state) => {
                return React.useMemo(
                    () => ({
                        ...state,
                        pageIndex: currentPage,
                    }),
                    [state, currentPage]
                );
            },
            initialState: { pageIndex: currentPage }, // Pass our hoisted table state
            manualPagination: true,
            pageCount: totalPage,
        },
        useSortBy,
        usePagination,
    );

    return (
        <>
            <Table {...getTableProps()}
                size='sm'
                rowGap={3}
                colorScheme={'teal'}
                borderRadius={'sm'}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())}
                                    color={'teal'}
                                    textAlign={column.id === 'lowest_price' ? 'right' : 'left'}>
                                    {column.render("Header")}
                                    <chakra.span pl='4'>
                                        {column.isSorted ? (
                                            column.isSortedDesc ? (
                                                <TriangleDownIcon aria-label='sorted descending' />
                                            ) : (
                                                <TriangleUpIcon aria-label='sorted ascending' />
                                            )
                                        ) : null}
                                    </chakra.span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                        prepareRow(row);
                        return (
                            <Tr {...row.getRowProps()} py={5}>
                                {row.cells.map((cell) => {
                                    return (
                                        <Td {...cell.getCellProps()}
                                            textAlign={cell.column.id === 'lowest_price' ? 'right' : 'left'} color={'teal'}>{cell.render("Cell")} </Td>
                                    );
                                })}
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>

            <Flex justifyContent="space-between" m={4} alignItems="center">
                <Flex>
                    <Tooltip label="First Page">
                        <IconButton
                            aria-label=''
                            onClick={() => setPage(1)}
                            isDisabled={currentPage === 1}
                            icon={<ArrowLeftIcon h={3} w={3} />}
                            mr={4}
                        />
                    </Tooltip>
                    <Tooltip label="Previous Page">
                        <IconButton
                            aria-label='Search database'
                            onClick={() => {
                                setPage((s: number) => (s === 0 ? 0 : s - 1));
                            }}
                            isDisabled={currentPage === 1}
                            icon={<ChevronLeftIcon h={6} w={6} />}
                        />
                    </Tooltip>
                </Flex>

                <Flex alignItems="center">
                    <Text flexShrink="0" mr={8}>
                        Page{" "}
                        <Text fontWeight="bold" as="span">
                            {pageIndex}
                        </Text>{" "}
                        of{" "}
                        <Text fontWeight="bold" as="span">
                            {pageOptions.length}
                        </Text>
                    </Text>

                    <Text flexShrink="0">Go to page:</Text>{" "}
                    <NumberInput
                        ml={2}
                        mr={8}
                        w={28}
                        min={1}
                        max={totalPage}
                        defaultValue={pageIndex}
                        onChange={(value: any) => {
                            const page = value ? Number(value) : 1;
                            setPage(page);
                        }}
                    >
                        <NumberInputField />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                </Flex>

                <Flex>
                    <Tooltip label="Next Page">
                        <IconButton
                            aria-label=''
                            onClick={() => {
                                setPage((s: number) => s + 1);
                            }}
                            isDisabled={currentPage === totalPage}
                            icon={<ChevronRightIcon h={6} w={6} />}
                        />
                    </Tooltip>
                    <Tooltip label="Last Page">
                        <IconButton
                            aria-label=''
                            onClick={() => {
                                setPage(totalPage);
                            }}
                            isDisabled={currentPage === totalPage}
                            icon={<ArrowRightIcon h={3} w={3} />}
                            ml={4}
                        />
                    </Tooltip>
                </Flex>
            </Flex>
        </>
    );
}

// export default CustomTable;
